/**
 * Dynamic Date Utilities
 * Date formatting functions that respect the current i18n language setting
 */
import { format, parseISO } from "date-fns";
import { tr } from "date-fns/locale";
import { enUS } from "date-fns/locale";
import i18n from "@/i18n";

/**
 * Get the correct date-fns locale based on current i18n language
 */
export const getDateLocale = () => {
  const lang = (i18n.language || "tr").toLowerCase();
  if (lang.startsWith("en")) return enUS;
  return tr;
};

/**
 * Format date to dd/MM/yyyy
 */
export const formatDateTR = (date) => {
  if (!date) return "-";
  try {
    const d = typeof date === "string" ? parseISO(date) : date;
    return format(d, "dd/MM/yyyy", { locale: getDateLocale() });
  } catch {
    return date?.split?.("T")?.[0] || "-";
  }
};

/**
 * Format date with weekday
 */
export const formatDateWithDayTR = (date) => {
  if (!date) return "-";
  try {
    const d = typeof date === "string" ? parseISO(date) : date;
    return format(d, "dd/MM/yyyy, EEEE", { locale: getDateLocale() });
  } catch {
    return date?.split?.("T")?.[0] || "-";
  }
};

/**
 * Format date to long format (e.g., "14 January 2026" or "14 Ocak 2026")
 */
export const formatDateLongTR = (date) => {
  if (!date) return "-";
  try {
    const d = typeof date === "string" ? parseISO(date) : date;
    return format(d, "d MMMM yyyy", { locale: getDateLocale() });
  } catch {
    return date?.split?.("T")?.[0] || "-";
  }
};

/**
 * Format date to full format with day name
 */
export const formatDateFullTR = (date) => {
  if (!date) return "-";
  try {
    const d = typeof date === "string" ? parseISO(date) : date;
    return format(d, "d MMMM yyyy, EEEE", { locale: getDateLocale() });
  } catch {
    return date?.split?.("T")?.[0] || "-";
  }
};

/**
 * Format datetime
 */
export const formatDateTimeTR = (date) => {
  if (!date) return "-";
  try {
    const d = typeof date === "string" ? parseISO(date) : date;
    return format(d, "dd/MM/yyyy HH:mm", { locale: getDateLocale() });
  } catch {
    return "-";
  }
};

/**
 * Format time only (HH:mm)
 */
export const formatTimeTR = (date) => {
  if (!date) return "-";
  try {
    const d = typeof date === "string" ? parseISO(date) : date;
    return format(d, "HH:mm", { locale: getDateLocale() });
  } catch {
    return "-";
  }
};

/**
 * Format month and year based on current language
 */
export const formatMonthYearTR = (month, year) => {
  const lang = i18n.language || "tr";
  const monthsTR = [
    "Ocak", "Şubat", "Mart", "Nisan", "Mayıs", "Haziran",
    "Temmuz", "Ağustos", "Eylül", "Ekim", "Kasım", "Aralık"
  ];
  const monthsEN = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];
  const months = lang === "en" ? monthsEN : monthsTR;
  return `${months[month - 1]} ${year}`;
};

/**
 * Get day names (short) based on current language
 */
export const getDayNamesShort = () => {
  const lang = i18n.language || "tr";
  if (lang === "en") return ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
  return ["Pzt", "Sal", "Çar", "Per", "Cum", "Cmt", "Paz"];
};

/**
 * Get day names (full) based on current language
 */
export const getDayNamesFull = () => {
  const lang = i18n.language || "tr";
  if (lang === "en") return ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
  return ["Pazartesi", "Salı", "Çarşamba", "Perşembe", "Cuma", "Cumartesi", "Pazar"];
};

/**
 * Get month names based on current language
 */
export const getMonthNames = () => {
  const lang = i18n.language || "tr";
  if (lang === "en") return [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];
  return [
    "Ocak", "Şubat", "Mart", "Nisan", "Mayıs", "Haziran",
    "Temmuz", "Ağustos", "Eylül", "Ekim", "Kasım", "Aralık"
  ];
};

// Keep backward-compatible exports
export const dayNamesTR = ["Pzt", "Sal", "Çar", "Per", "Cum", "Cmt", "Paz"];
export const dayNamesFullTR = ["Pazartesi", "Salı", "Çarşamba", "Perşembe", "Cuma", "Cumartesi", "Pazar"];
export const monthNamesTR = [
  "Ocak", "Şubat", "Mart", "Nisan", "Mayıs", "Haziran",
  "Temmuz", "Ağustos", "Eylül", "Ekim", "Kasım", "Aralık"
];

/**
 * Convert a Date object to 'YYYY-MM-DD' in LOCAL timezone.
 * Avoids the off-by-one bug of toISOString() (which uses UTC and shifts the
 * date back a day for timezones ahead of UTC, e.g. Turkey UTC+3).
 */
export const toLocalDateString = (date) => {
  if (!date || !(date instanceof Date) || isNaN(date.getTime())) return "";
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

/**
 * Today's date as 'YYYY-MM-DD' in LOCAL timezone.
 */
export const todayLocalString = () => toLocalDateString(new Date());

/**
 * Parse date string (dd/MM/yyyy) to Date object
 */
export const parseDateTR = (dateStr) => {
  if (!dateStr) return null;
  try {
    const [day, month, year] = dateStr.split("/");
    return new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
  } catch {
    return null;
  }
};

export default {
  getDateLocale,
  formatDateTR,
  formatDateWithDayTR,
  formatDateLongTR,
  formatDateFullTR,
  formatDateTimeTR,
  formatTimeTR,
  formatMonthYearTR,
  getDayNamesShort,
  getDayNamesFull,
  getMonthNames,
  dayNamesTR,
  dayNamesFullTR,
  monthNamesTR,
  parseDateTR,
  toLocalDateString,
  todayLocalString,
};
